/**
 * @author: Hadeer Elbanna
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2020-10-25 02:40:36
 * @description Implementation of the ExternalNavbar React component .
 * @filename external-navbar.tsx
 */

import React from "react";
import $ from "jquery";
import { Router } from "../../../utilities/router";
import { SVG } from "../../../component-configuration/svgs";
import { snowplowAnalytics } from "../../../utilities/general";

const LOGO = require("../../../../static/rv-logo-on-black.png");

/**
 * The state of the ExternalNavbar component.
 *
 * @export
 * @interface
 */
interface ExternalNavbarState {
  /**
   * save the active displayed page form current page url
   *
   * @type {string}
   * @memberof ExternalNavbarState
   */
  activePage: string;

  /**
   * A flag which is true if the hamburger drop-down is being displayed.
   *
   * @type {boolean}
   *
   */
  hamburgerDropDown: boolean;

  /**
   * The height of the navbar in pixels.
   *
   * @type {number}
   */
  heightInPixels: number;

  /**
   * A flag which, if set to true, sets the navbar to use transparent background.
   *
   * @type {boolean}
   */
  transparent: boolean;
}

/**
 *  A React component that renders the ExternalNavbar.
 *
 * @export
 * @class ExternalNavbar
 * @extends {React.Component<ExternalNavbarProps, ExternalNavbarState>}
 */
export class ExternalNavbar extends React.Component<{}, ExternalNavbarState> {
  /**
   * The navbar element.
   *
   * @type {HTMLElement}
   */
  navbar: HTMLElement;

  /**
   * save the value of window scrollTop
   *
   * @type {number}
   */
  windowScrollValue: number;

  /**
   * Constructor
   *
   * @param {ExternalNavbarProps} props The component props.
   */
  constructor(props: {}) {
    super(props);
    this.windowScrollValue = 0;

    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onHashChange = this.onHashChange.bind(this);

    this.state = {
      activePage: "",
      hamburgerDropDown: false,
      heightInPixels: 1,
      transparent: true,
    };
  }

  onScroll() {
    if (this.navbar !== undefined && this.navbar !== null) {
      if (document.documentElement.scrollTop > 100) {
        this.setState({
          transparent: false,
        });
      } else {
        this.setState({
          transparent: true,
        });
      }
    }
  }
  /**
   * Invoked by the React framework immediately after a component is mounted.
   */
  componentDidMount() {
    window.addEventListener("hashchange", this.onHashChange, false);
    this.onHashChange();

    window.addEventListener("resize", this.onResize.bind(this));
    this.onResize();

    window.addEventListener("scroll", this.onScroll.bind(this));

    $(".collapsable").on("click", (e) => {
      e.preventDefault();
      const expanded: string = e.currentTarget.getAttribute("data-expanded");
      const target: string = e.currentTarget.getAttribute("href");
      if (expanded === "true") {
        $(target).animate({ height: "0px" }, 300, () => {
          $(target).css("display", "none");
        });
        e.currentTarget.setAttribute("data-expanded", "false");
      } else {
        $(target).css("display", "block");
        $(target).css("height", "auto");
        const height: number = $(target).height();
        $(target).css("height", "0");
        $(target).animate({ height }, 300);
        e.currentTarget.setAttribute("data-expanded", "true");
      }
    });
  }

  /**
   * Invoked when the windows address fragment changes.
   */
  onHashChange(): void {
    Router.getAlias().then((alias: string) => {
      this.setState({ activePage: alias });
    });
    Router.getLocation().then((location: string) => {
      if (location) {
        this.setState({ activePage: location });
      }
    });
  }

  /**
   * Invoked just before the component is mounted.
   */
  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize.bind(this));
    window.removeEventListener("hashchange", this.onHashChange.bind(this));
    window.removeEventListener("scroll", this.onScroll.bind(this));
    $(window).scrollTop(this.windowScrollValue);
    $("body").addClass("allow-scroll");
  }

  /**
   * invoked when component changed
   */
  componentDidUpdate(prevProps, prevState: ExternalNavbarState) {
    if (prevState.hamburgerDropDown !== this.state.hamburgerDropDown) {
      if (this.state.hamburgerDropDown) {
        this.windowScrollValue = document.documentElement.scrollTop;
        $("body").removeClass("allow-scroll");
        document.body.style.marginTop = `-${this.windowScrollValue}px`;
      } else {
        document.body.style.marginTop = "0";
        $("body").addClass("allow-scroll");
        window.scrollTo(0, this.windowScrollValue);
      }
    }
    if (prevState.heightInPixels !== this.state.heightInPixels) {
      this.onResize();
    }
  }

  /**
   * A function invoked on nav item click)
   *
   * @type {function(event: React.MouseEvent<HTMLElement>, index: number, alias: string, queryParameters?: string[], location?: string))}
   */
  handleClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
    alias: string,
    queryParameters?: string[],
    location?: string
  ) => {
    this.setState({ hamburgerDropDown: false });
    Router.to(alias, queryParameters, location);
    this.setState({
      hamburgerDropDown: false,
    });
    snowplowAnalytics().then((r) => {
      r.page({
        path: "/" + alias + (location ? "#" + location : ""),
        url:
          window.location.protocol +
          "//" +
          window.location.host +
          "/" +
          alias +
          (location ? "#" + location : ""),
      });
    });
  };

  /**
   * Invoked when the window is resized.
   */
  onResize(): void {
    if (this.navbar !== undefined && this.navbar !== null) {
      this.navbar.parentElement.style.height = `${this.navbar.clientHeight}px`;
      this.setState({
        heightInPixels: this.navbar.clientHeight,
      });
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div>
        <nav
          className={`external main-navbar navbar navbar-expand-md navbar-dark fixed-top 
                ${this.state.transparent ? "transparent" : ""}`}
          id="top-nav"
          ref={(navbar) => {
            this.navbar = navbar;
          }}
        >
          <div className="adjustNav">
            <a className="navbar-brand nav-logo" href="/">
              <img
                src={LOGO}
                alt="Radivision logo"
                className="logo"
                onLoad={this.onResize}
              />
            </a>
            <div
              className={`collapse navbar-collapse${
                this.state.hamburgerDropDown ? " expanded" : ""
              }`}
              id="navbarSupportedContent"
            >
              <div
                className="navbar-collapse-backdrop d-block d-lg-none"
                onClick={() => this.setState({ hamburgerDropDown: false })}
              />
              <ul className="navbar-nav">
                <li className="nav-item d-block d-lg-none">
                  <a
                    className={`nav-link${this.state.activePage === "home" ? " active" : ""}`}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      this.handleClick(
                        event,
                        -1,
                        "home",
                        undefined,
                        undefined
                      )
                    }
                  >
                    Home
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link${
                      this.state.activePage === "about-us" ? " active" : ""
                    }`}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      this.handleClick(
                        event,
                        -1,
                        "about-us",
                        undefined,
                        undefined
                      );
                      window.location.hash = "";
                      window.scrollTo({top: 0});
                    }
                    }
                  >
                    About
                  </a>
                </li>
                <li className="nav-item">
                      <a
                        className={`nav-link${
                          window.location.hash === "#:team" ? " active" : ""
                        }`}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                          return this.handleClick(
                            event,
                            -1,
                            "about-us",
                            undefined,
                            "team"
                          );
                        }}
                      >
                        Team
                      </a>
                </li>
                { false && (<>
                <li className="nav-item">
                  <a
                    className={`nav-link${
                      this.state.activePage === "contact-us" ? " active" : ""
                    }`}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      this.handleClick(
                        event,
                        -1,
                        "support",
                        undefined,
                        "contact-us"
                      )
                    }
                  >
                    Contact
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link${
                      this.state.activePage === "faqs" ? " active" : ""
                    }`}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      this.handleClick(event, -1, "support", undefined, "faqs")
                    }
                  >
                    FAQs
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link${
                      this.state.activePage === "report-an-issue"
                        ? " active"
                        : ""
                    }`}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      this.handleClick(
                        event,
                        -1,
                        "support",
                        undefined,
                        "report-an-issue"
                      )
                    }
                  >
                    Report an Issue
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link${
                      this.state.activePage === "filming" ? " active" : ""
                    }`}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      this.handleClick(
                        event,
                        -1,
                        "filming",
                        undefined,
                        undefined
                      )
                    }
                  >
                    Filming
                  </a>
                </li>
                <li className="nav-item dropdown">
                  <a
                    className="nav-link dropdown-toggle more-dropdown"
                    id="moreDropdownMenu"
                    role="button"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <span className="d-none d-lg-block">{SVG.dotsThin}</span>
                    <span className="d-block d-lg-none">
                      More<i>{SVG.caretRight}</i>
                    </span>
                  </a>
                  <div
                    className="dropdown-menu"
                    aria-labelledby="moreDropdownMenu"
                  >
                    <a
                      className="dropdown-item"
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        if (this.state.activePage === "privacy-policy") {
                          return Router.scrollTo("cookies-section");
                        }
                        return this.handleClick(
                          event,
                          -1,
                          "legal",
                          ["section=cookies-section"],
                          "privacy-policy"
                        );
                      }}
                    >
                      Cookies
                    </a>
                    <a
                      className={`dropdown-item${
                        this.state.activePage === "privacy-policy"
                          ? " active"
                          : ""
                      }`}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        return this.handleClick(
                          event,
                          -1,
                          "legal",
                          undefined,
                          "privacy-policy"
                        );
                      }}
                    >
                      Privacy
                    </a>
                    <a
                      className={`dropdown-item${
                        this.state.activePage === "terms-conditions"
                          ? " active"
                          : ""
                      }`}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        return this.handleClick(
                          event,
                          -1,
                          "legal",
                          undefined,
                          "terms-conditions"
                        );
                      }}
                    >
                      Terms
                    </a>
                    <a
                      className="dropdown-item"
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        return this.handleClick(
                          event,
                          -1,
                          "support",
                          undefined,
                          "technical-issues"
                        );
                      }}
                    >
                      Technical Issues
                    </a>
                  </div>
                </li>
              </>)}
              </ul>
            </div>
            <div className="nav-right-menu d-flex align-items-center">
              <ul className="navbar-nav">
                <li className="nav-item">
                  <a
                    className="nav-link semibold join"
                    href={`${process.env.HOME_URI}`}
                  >
                    radivision.com
                    {SVG.goToSite}
                  </a>
                </li>
              </ul>
            </div>
            <button
              className={`hamburger hamburger--collapse navbar-toggler${
                this.state.hamburgerDropDown ? " is-active" : ""
              }`}
              type="button"
              aria-label="Toggle navigation"
              onClick={() =>
                this.setState({
                  hamburgerDropDown: !this.state.hamburgerDropDown,
                })
              }
            >
              <span className="hamburger-box">
                <span className="hamburger-inner" />
              </span>
            </button>
          </div>
        </nav>
      </div>
    );
  }
}
